import type { ItemModifierDefinition, ItemTemplate } from '@mmo/schemas';

/**
 * Item templates. A template is the authored definition ("Iron Longsword"); every concrete copy in
 * the world is an ItemInstance with its own UUID. Template IDs are persisted on instances, so
 * never rename or reuse a shipped ID — deprecate it instead (tag `deprecated`).
 *
 * Naming: `<category>.<type>.<name>`.
 */

type GearInput = Pick<
  ItemTemplate,
  'id' | 'name' | 'category' | 'rarityId' | 'itemLevel' | 'requiredLevel'
> & {
  equipment: Omit<NonNullable<ItemTemplate['equipment']>, 'weapon'> & {
    weapon?: NonNullable<ItemTemplate['equipment']>['weapon'];
  };
  binding?: ItemTemplate['binding'];
  allowedClassIds?: string[];
  vendorValue?: number;
  description?: string;
};

const gear = (g: GearInput): ItemTemplate => {
  const binding = g.binding ?? 'on_equip';
  const tradeable = binding === 'none' || binding === 'on_equip';
  return {
    description: g.description ?? '',
    allowedClassIds: g.allowedClassIds ?? [],
    binding,
    maxStack: 1,
    tradeable,
    marketplaceAllowed: tradeable,
    vaultAllowed: true,
    accountVaultAllowed: binding !== 'on_pickup',
    vendorValue: g.vendorValue ?? 100,
    iconId: `icons/items/${g.id}`,
    modelId: `models/items/${g.id}`,
    consumable: null,
    questId: null,
    goesToMaterialPouch: false,
    tags: [],
    ...g,
    equipment: { weapon: null, ...g.equipment },
  };
};

export const itemTemplates: ItemTemplate[] = [
  gear({
    id: 'accessory.cloak.oathkeepers_mantle',
    name: 'Oathkeeper’s Mantle',
    description:
      'The restored mantle of the Last Door keepers. Their oath was to preserve the dream.',
    category: 'accessory',
    rarityId: 'rare',
    itemLevel: 7,
    requiredLevel: 3,
    binding: 'on_pickup',
    equipment: {
      slotType: 'cloak',
      equipmentTypeId: 'cloak',
      maxDurability: null,
      baseStats: {
        stamina: { min: 6, max: 6 },
        armor: { min: 6, max: 6 },
        strength: { min: 3, max: 3 },
        intellect: { min: 3, max: 3 },
      },
    },
  }),
  gear({
    id: 'accessory.necklace.springward_pendant',
    name: 'Springward Pendant',
    description:
      'Maren’s pendant, earned by tracing the inward-facing well seals. The Last Door is still closed.',
    category: 'accessory',
    rarityId: 'uncommon',
    itemLevel: 5,
    requiredLevel: 3,
    binding: 'on_pickup',
    equipment: {
      slotType: 'necklace',
      equipmentTypeId: 'necklace',
      baseStats: { stamina: { min: 4, max: 4 }, armor: { min: 3, max: 3 } },
      maxDurability: null,
    },
  }),
  gear({
    id: 'accessory.ring.stillwater_seal',
    name: 'Stillwater Seal',
    description: 'Maren’s warden seal, entrusted to the traveller who quieted the eastern water.',
    category: 'accessory',
    rarityId: 'uncommon',
    itemLevel: 5,
    requiredLevel: 3,
    binding: 'on_pickup',
    equipment: {
      slotType: 'ring',
      equipmentTypeId: 'ring',
      baseStats: {
        armor: { min: 3, max: 3 },
        stamina: { min: 4, max: 4 },
        strength: { min: 3, max: 3 },
        intellect: { min: 3, max: 3 },
      },
      maxDurability: null,
    },
  }),
  gear({
    id: 'accessory.cloak.rootward_mantle',
    name: 'Rootward Mantle',
    description: "An old keeper's mantle, earned by silencing the light beneath the Hollow.",
    category: 'accessory',
    rarityId: 'uncommon',
    itemLevel: 4,
    requiredLevel: 2,
    binding: 'on_pickup',
    equipment: {
      slotType: 'cloak',
      equipmentTypeId: 'cloak',
      baseStats: { armor: { min: 9, max: 9 }, stamina: { min: 3, max: 3 } },
      maxDurability: null,
    },
  }),
  gear({
    id: 'accessory.trinket.keepers_token',
    name: "Keeper's Ward-Token",
    description: "Rill's small carved ward. The roots beneath the Hollow still stir.",
    category: 'accessory',
    rarityId: 'uncommon',
    itemLevel: 3,
    requiredLevel: 2,
    binding: 'on_pickup',
    equipment: {
      slotType: 'trinket',
      equipmentTypeId: 'trinket',
      baseStats: { stamina: { min: 3, max: 3 }, armor: { min: 4, max: 4 } },
      maxDurability: null,
    },
  }),
  gear({
    id: 'weapon.sword.iron_longsword',
    name: 'Iron Longsword',
    description: 'A plain but dependable blade.',
    category: 'weapon',
    rarityId: 'common',
    itemLevel: 5,
    requiredLevel: 1,
    binding: 'none',
    equipment: {
      slotType: 'one_hand',
      equipmentTypeId: 'sword',
      baseStats: { strength: { min: 2, max: 4 }, attack_power: { min: 6, max: 9 } },
      maxDurability: 80,
      weapon: { minDamage: 7, maxDamage: 13, attackSpeedMs: 2400 },
    },
    vendorValue: 150,
  }),
  gear({
    id: 'weapon.staff.oak_staff',
    name: 'Oak Staff',
    category: 'weapon',
    rarityId: 'common',
    itemLevel: 5,
    requiredLevel: 1,
    equipment: {
      slotType: 'two_hand',
      equipmentTypeId: 'staff',
      baseStats: { intellect: { min: 3, max: 5 }, spell_power: { min: 8, max: 12 } },
      maxDurability: 70,
      weapon: { minDamage: 9, maxDamage: 16, attackSpeedMs: 3200 },
    },
  }),
  gear({
    id: 'weapon.bow.hunting_bow',
    name: 'Hunting Bow',
    category: 'weapon',
    rarityId: 'uncommon',
    itemLevel: 8,
    requiredLevel: 5,
    equipment: {
      slotType: 'ranged',
      equipmentTypeId: 'bow',
      baseStats: { agility: { min: 3, max: 6 }, attack_power: { min: 8, max: 12 } },
      maxDurability: 70,
      weapon: { minDamage: 10, maxDamage: 18, attackSpeedMs: 2800 },
    },
  }),
  gear({
    id: 'armor.shield.oak_buckler',
    name: 'Oak Buckler',
    category: 'armor',
    rarityId: 'common',
    itemLevel: 4,
    requiredLevel: 1,
    equipment: {
      slotType: 'off_hand',
      equipmentTypeId: 'shield',
      baseStats: { armor: { min: 20, max: 30 }, block: { min: 2, max: 3 } },
      maxDurability: 90,
    },
  }),
  gear({
    id: 'armor.plate.recruit_breastplate',
    name: 'Recruit Breastplate',
    category: 'armor',
    rarityId: 'common',
    itemLevel: 5,
    requiredLevel: 1,
    equipment: {
      slotType: 'chest',
      equipmentTypeId: 'plate',
      baseStats: { armor: { min: 40, max: 50 }, stamina: { min: 2, max: 3 } },
      maxDurability: 100,
    },
  }),
  gear({
    id: 'armor.leather.trapper_cap',
    name: "Trapper's Cap",
    category: 'armor',
    rarityId: 'common',
    itemLevel: 3,
    requiredLevel: 1,
    equipment: {
      slotType: 'head',
      equipmentTypeId: 'leather',
      baseStats: { armor: { min: 10, max: 14 }, agility: { min: 1, max: 2 } },
      maxDurability: 60,
    },
  }),
  gear({
    id: 'armor.cloth.apprentice_robe',
    name: 'Apprentice Robe',
    category: 'armor',
    rarityId: 'common',
    itemLevel: 3,
    requiredLevel: 1,
    allowedClassIds: ['class.mage', 'class.cleric'],
    equipment: {
      slotType: 'chest',
      equipmentTypeId: 'cloth',
      baseStats: { armor: { min: 6, max: 9 }, intellect: { min: 2, max: 3 } },
      maxDurability: 50,
    },
  }),
  gear({
    id: 'accessory.ring.copper_band',
    name: 'Copper Band',
    category: 'accessory',
    rarityId: 'uncommon',
    itemLevel: 6,
    requiredLevel: 3,
    equipment: {
      slotType: 'ring',
      equipmentTypeId: 'ring',
      baseStats: { stamina: { min: 2, max: 3 } },
      maxDurability: null,
    },
  }),
  gear({
    id: 'accessory.cloak.wayfarer_cloak',
    name: "Wayfarer's Cloak",
    category: 'accessory',
    rarityId: 'common',
    itemLevel: 4,
    requiredLevel: 1,
    equipment: {
      slotType: 'cloak',
      equipmentTypeId: 'cloak',
      baseStats: { armor: { min: 5, max: 7 } },
      maxDurability: null,
    },
  }),
  gear({
    id: 'accessory.trinket.ember_heart',
    name: 'Ember Heart',
    description: 'Warm to the touch. Bound to whoever claims it.',
    category: 'accessory',
    rarityId: 'epic',
    itemLevel: 20,
    requiredLevel: 15,
    binding: 'on_pickup',
    equipment: {
      slotType: 'trinket',
      equipmentTypeId: 'trinket',
      baseStats: { spell_power: { min: 18, max: 24 } },
      maxDurability: null,
    },
  }),
  {
    id: 'consumable.potion.minor_healing',
    name: 'Minor Healing Potion',
    description: 'Restores a small amount of health.',
    category: 'consumable',
    rarityId: 'common',
    itemLevel: 1,
    requiredLevel: 1,
    allowedClassIds: [],
    binding: 'none',
    maxStack: 20,
    tradeable: true,
    marketplaceAllowed: true,
    vaultAllowed: true,
    accountVaultAllowed: true,
    vendorValue: 25,
    iconId: 'icons/items/consumable.potion.minor_healing',
    modelId: null,
    equipment: null,
    consumable: {
      cooldownGroup: 'potion',
      cooldownMs: 60_000,
      effects: [{ type: 'heal', amount: 60 }],
    },
    questId: null,
    goesToMaterialPouch: false,
    tags: [],
  },
  {
    id: 'material.ore.copper_ore',
    name: 'Copper Ore',
    description: 'Raw ore used in blacksmithing.',
    category: 'material',
    rarityId: 'common',
    itemLevel: 1,
    requiredLevel: 1,
    allowedClassIds: [],
    binding: 'none',
    maxStack: 200,
    tradeable: true,
    marketplaceAllowed: true,
    vaultAllowed: true,
    accountVaultAllowed: true,
    vendorValue: 5,
    iconId: 'icons/items/material.ore.copper_ore',
    modelId: 'models/props/ore_chunk',
    equipment: null,
    consumable: null,
    questId: null,
    goesToMaterialPouch: true,
    tags: ['ore'],
  },
  {
    id: 'material.hide.wolf_pelt',
    name: 'Wolf Pelt',
    description: 'Used in leatherworking.',
    category: 'material',
    rarityId: 'common',
    itemLevel: 1,
    requiredLevel: 1,
    allowedClassIds: [],
    binding: 'none',
    maxStack: 200,
    tradeable: true,
    marketplaceAllowed: true,
    vaultAllowed: true,
    accountVaultAllowed: true,
    vendorValue: 8,
    iconId: 'icons/items/material.hide.wolf_pelt',
    modelId: null,
    equipment: null,
    consumable: null,
    questId: null,
    goesToMaterialPouch: true,
    tags: ['hide'],
  },
  {
    id: 'quest.misc.elders_letter',
    name: "Elder's Letter",
    description: 'A sealed letter for the garrison captain.',
    category: 'quest',
    rarityId: 'common',
    itemLevel: 1,
    requiredLevel: 1,
    allowedClassIds: [],
    binding: 'on_pickup',
    maxStack: 1,
    tradeable: false,
    marketplaceAllowed: false,
    vaultAllowed: false,
    accountVaultAllowed: false,
    vendorValue: 0,
    iconId: 'icons/items/quest.misc.elders_letter',
    modelId: null,
    equipment: null,
    consumable: null,
    questId: 'quest.greenvale.letter_to_captain',
    goesToMaterialPouch: false,
    tags: [],
  },
  {
    id: 'mount.horse.chestnut_horse',
    name: 'Reins of the Chestnut Horse',
    description: 'Teaches the Chestnut Horse mount. (Mount system not implemented.)',
    category: 'mount',
    rarityId: 'rare',
    itemLevel: 20,
    requiredLevel: 20,
    allowedClassIds: [],
    binding: 'account',
    maxStack: 1,
    tradeable: false,
    marketplaceAllowed: false,
    vaultAllowed: true,
    accountVaultAllowed: true,
    vendorValue: 0,
    iconId: 'icons/items/mount.horse.chestnut_horse',
    modelId: null,
    equipment: null,
    consumable: null,
    questId: null,
    goesToMaterialPouch: false,
    tags: [],
  },
];

export const itemModifiers: ItemModifierDefinition[] = [
  {
    id: 'mod.of_the_bear',
    name: 'of the Bear',
    stat: 'stamina',
    range: { min: 1, max: 4 },
    allowedCategories: ['weapon', 'armor', 'accessory'],
  },
  {
    id: 'mod.of_the_tiger',
    name: 'of the Tiger',
    stat: 'agility',
    range: { min: 1, max: 4 },
    allowedCategories: ['weapon', 'armor', 'accessory'],
  },
  {
    id: 'mod.of_the_owl',
    name: 'of the Owl',
    stat: 'intellect',
    range: { min: 1, max: 4 },
    allowedCategories: ['weapon', 'armor', 'accessory'],
  },
  {
    id: 'mod.of_striking',
    name: 'of Striking',
    stat: 'crit_rating',
    range: { min: 2, max: 6 },
    allowedCategories: ['weapon', 'accessory'],
  },
  {
    id: 'mod.of_haste',
    name: 'of Haste',
    stat: 'haste_rating',
    range: { min: 2, max: 6 },
    allowedCategories: ['weapon', 'accessory'],
  },
];
